import { ref } from 'vue';
import Sequence from '@/sequence';
import useFlows from '@/composables/useFlows';
import useConfig from '@/composables/useConfig';
import useActions from '@/composables/useActions';
import useContent from '@/composables/useContent';
import { START_FLOW_ID, RETURN_FLOW_ID } from '@/js/constants';

const currentSeq = ref(null);

export default function useSequence() {
  const { currentAction } = useActions();
  const { pauseContent, resumeContent } = useContent();
  const { popReturnStack } = useConfig();

  async function runRawSequence(id, seqItems) {
    const { languageFlow } = useFlows();
    const seq = seqItems || languageFlow.value[id];
    if (!id || !seq?.length) {
      return;
    }

    const sequence = new Sequence(seq, id);

    if (!currentSeq.value || currentSeq.value.willComplete || currentSeq.value.completed) {
      currentSeq.value = sequence;
      await currentSeq.value.run();
    } else {
      currentSeq.value.fork = sequence;
    }
  }

  async function runSequence(id, seqItems) {
    if (id === RETURN_FLOW_ID) {
      const nextId = popReturnStack();
      if (nextId) {
        return runSequence(nextId);
      }
      return null;
    }
    return runRawSequence(id, seqItems);
  }

  function interruptSequence(reset = true) {
    if (currentSeq.value) {
      currentSeq.value.stop(reset);
    }
  }

  function pauseSequence() {
    if (currentSeq.value) {
      currentSeq.value.pause(true);
      pauseContent();

      if (currentAction.value.pause) {
        currentAction.value.pause();
      }
    }
  }

  function resumeSequence() {
    if (currentSeq.value && !currentSeq.value.completed) {
      currentSeq.value.pause(false);
      resumeContent();

      if (currentAction.value.resume) {
        currentAction.value.resume();
      }

      return;
    }

    resumeContent();
  }

  function restartSequence(reset = true, seqItems = null) {
    if (currentSeq.value) {
      currentSeq.value.stop(reset);
      runSequence(currentSeq.value.id, seqItems);
    }
  }

  function navigateTo(id, elementIndex) {
    const { languageFlow } = useFlows();
    const seq = languageFlow.value[id] || [];
    const index = seq.findIndex((item) => item.action === 'navigation' && item.params.uuid === `${id}_${elementIndex}`);
    const newSeq = index > -1 ? seq.slice(index) : [];
    if (newSeq.length) {
      runSequence(id, newSeq);
    } else {
      runSequence(START_FLOW_ID);
    }
  }

  return {
    runSequence,
    runRawSequence,
    interruptSequence,
    currentSeq,
    pauseSequence,
    resumeSequence,
    restartSequence,
    navigateTo,
  };
}
